import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import ScheduleItem from "./ScheduleItem";
import { TypographyLarge, TypographyNormal } from "../customComponent";
import { getSchedules } from "../../store/actions/scheduleAction";
import { colorStyling } from "../../helpers/color";
import { filterObjtoString } from "../../helpers/filterHelper";
import {
  SET_LOADING_SCHEDULE,
  SET_SCHEDULE_PAGE,
  SET_SCHEDULE_ROWS_PER_PAGE,
} from "../../store/actionTypes/scheduleActionType";
import { parseVariables } from "../../helpers/localize";
import ContentDetails from "../cms/contentTemplate/detail/ContentDetails";

const ScheduleList = () => {
  const dispatch = useDispatch();
  const {
    schedules,
    scheduleFilters,
    loadingSchedule: loading,
    page,
    rowsPerPage,
  } = useSelector((state) => state.schedule);

  const { t } = useTranslation("schedule");
  const { t: tCommon } = useTranslation();

  const tableDiv = useRef(null);
  const scrollRef = useRef(null);
  const fetchOnFirstLoad = useRef(false);

  const [openContentDetail, setOpenContentDetail] = useState(false);
  const [contentDetailId, setContentDetailId] = useState(null);

  const handleOpenContentDetail = (id) => {
    setContentDetailId(id);
    setOpenContentDetail(true);
  };

  const handleCloseContentDetail = () => {
    setOpenContentDetail(false);
  };

  const handleLoading = (value) => {
    dispatch({ type: SET_LOADING_SCHEDULE, payload: value });
  };

  const handleChangePagination = (e, page) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    dispatch({ type: SET_SCHEDULE_PAGE, payload: page });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: SET_SCHEDULE_ROWS_PER_PAGE,
      payload: parseInt(event.target.value, 10),
    });
    handleChangePagination(null, 0);
  };

  const fetchData = async (filterObj) => {
    let isPrefetch = false;
    let limit = rowsPerPage;
    let offset = page * rowsPerPage;
    if (offset === 0) {
      limit = limit * 2; //fetch 2 page worth of data
      isPrefetch = false;
    } else {
      offset = (page + 1) * rowsPerPage;
      isPrefetch = true;
    }
    let filterString = filterObjtoString(filterObj, limit, offset);

    const schedulesLength = schedules?.items?.length;
    if (schedulesLength === 0) handleLoading(true);
    if (
      schedulesLength !== schedules.totalItem ||
      schedulesLength === 0 ||
      offset === 0
    )
      dispatch(getSchedules(filterString, isPrefetch, handleLoading));
  };

  useEffect(() => {
    if (fetchOnFirstLoad.current) {
      fetchData(scheduleFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchData(scheduleFilters);
      fetchOnFirstLoad.current = true;
    } else handleChangePagination(null, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleFilters]);

  const displayedRowsLabel = ({ from, to, count }) => {
    return parseVariables(tCommon("table.displayedRowsLabel"), {
      prefix: `${from}–${to}`,
      suffix:
        count !== -1
          ? count
          : `${tCommon("table.displayedRowsMoreThan")} ${to}`,
    });
  };

  return (
    <>
      <ContentDetails
        open={openContentDetail}
        handleClose={handleCloseContentDetail}
        contentID={contentDetailId}
      />
      <TableContainer
        ref={tableDiv}
        component={Paper}
        sx={{ overflowY: "auto", maxHeight: 444, boxShadow: 3 }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ height: "20vh" }}
          >
            <CircularProgress
              size={20}
              thickness={3}
              sx={{ color: colorStyling.primary }}
            />
            <TypographyNormal
              sx={{ color: colorStyling.primary, mt: 2, fontWeight: 300 }}
            >
              {tCommon("loadingDataList")}
            </TypographyNormal>
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="left" width={"5%"}></TableCell>
                <TableCell align="left" width={"20%"}>
                  {t("table.headerName")}
                </TableCell>
                <TableCell align="center" width={"10%"}>
                  {t("table.headerStatus")}
                </TableCell>
                <TableCell align="left" width={"10%"}>
                  {t("table.headerFrequency")}
                </TableCell>
                <TableCell align="left" width={"30%"}>
                  {t("table.headerDate")}
                </TableCell>
                <TableCell align="left" width={"25%"}>
                  {t("table.headerAction")}
                </TableCell>
              </TableRow>
            </TableHead>
            {!schedules.items.length ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7}>
                    <Alert
                      severity="info"
                      variant="filled"
                      icon={false}
                      sx={{ textAlign: "center", display: "block" }}
                    >
                      <TypographyLarge sx={{ color: colorStyling.white.full }}>
                        {tCommon("noData")}
                      </TypographyLarge>
                    </Alert>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {schedules?.items
                  ?.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                  ?.map((schedule, index) => (
                    <ScheduleItem
                      key={schedule.id}
                      index={index}
                      schedule={schedule}
                      dispatch={dispatch}
                      openContent={handleOpenContentDetail}
                    />
                  ))}
              </TableBody>
            )}
          </Table>
        )}
      </TableContainer>

      {(!loading || schedules?.totalItem > 0) && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            pr: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Paper component="div" elevation={1} sx={{ px: 1 }}>
            <TablePagination
              component="div"
              count={schedules.totalItem}
              page={page}
              onPageChange={handleChangePagination}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={displayedRowsLabel}
              labelRowsPerPage={tCommon("table.rowsPerPageLabel")}
            />
          </Paper>
        </Box>
      )}
    </>
  );
};

export default ScheduleList;
